/** @typedef {import('../../types/compliance').KycDocument} KycDocument */
/** @typedef {import('../../types/compliance').KycUpload} KycUpload */

import { capitalize, formatErrorMessage } from '@basqet-dashboard/common/utils';
import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';

import {
  addKycComment,
  fetchAllKycUploads,
  fetchKycComments,
  fetchKycDocuments,
  fetchKycUploadByReference,
  fetchKycUploadsForMerchant,
  getKycInfo,
  reviewKycUpload
} from '../../api';
import { queryClient } from '../../config';
import { generatedDates, handleQueryError, mapKycsToMerchantSummaries } from '../../utils';

/**
 * @param {string} [type]
 * @returns {{ kycTypes: { label: string, value: string }[], kycDocuments: Object, isLoading: boolean }}
 */
const useFetchKycDocuments = (type = '') => {
  const {
    data: kycData = {},
    error,
    isLoading
  } = useQuery(['fetchKycAllDocuments', type], ({ queryKey }) => fetchKycDocuments(queryKey[1]));

  if (error) {
    formatErrorMessage(error);
    return;
  }

  const kycDocumentsKey = Object.keys(kycData);

  const kycTypes =
    kycDocumentsKey.map((documentType) => {
      const labelUppercase = documentType.replace(/_/g, ' ');

      const label = capitalize(labelUppercase);

      return {
        label,
        value: documentType
      };
    }) ?? [];

  const kycDocuments = kycData;

  return { kycTypes, kycDocuments, isLoading };
};

/** @returns {{ kycInfo: Object|null, isLoading: boolean, refetchKycInfo: Function }} */
const useKycInfo = () => {
  const {
    data,
    refetch: refetchKycInfo,
    isLoading,
    error
  } = useQuery('getKycInfo', getKycInfo, {
    retry: false,
    onError: (error) => {
      if (error?.response?.status !== 404) {
        formatErrorMessage(error);
      }
    }
  });

  const kycInfo = error?.response?.status === 404 ? null : data?.data ?? null;

  return { kycInfo, isLoading, refetchKycInfo };
};

// Admin KYC hooks
/** @returns {{ kycUploads: KycUpload[], kycMerchantSummaries: Object[], isFetching: boolean, isLoading: boolean, isFetchingNextPage: boolean, fetchNextPage: Function, hasNextPage: boolean, searchQuery: string, setSearchQuery: Function, isRefetching: boolean, setStatus: Function, status: string, setKycType: Function, kycType: string, filterByDate: Function, refetch: Function }} */
const useFetchKycUploads = () => {
  const [kycUploads, setKycUploads] = useState([]);
  const [kycMerchantSummaries, setKycMerchantSummaries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('');
  const [kycType, setKycType] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });

  const {
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isRefetching,
    data,
    error,
    refetch
  } = useInfiniteQuery(
    'allKycUploads',
    ({ pageParam }) =>
      fetchAllKycUploads({
        offset: pageParam,
        searchQuery,
        status,
        kyc_type: kycType,
        dateFilter
      }),
    {
      getNextPageParam: (lastPage) => {
        const { hasNext } = lastPage.meta ?? {};
        const lastKyc = lastPage && lastPage.data && lastPage.data[lastPage.data.length - 1];
        return hasNext ? lastKyc && lastKyc.id : undefined;
      }
    }
  );

  if (isError) {
    handleQueryError(error, 'kyc');
  }

  useEffect(() => {
    if (data && data.pages) {
      let aggregatedKycs = [];
      data.pages.map(({ data: kycsList = [] }) => {
        aggregatedKycs = [...aggregatedKycs, ...(Array.isArray(kycsList) ? kycsList : [])];
      });

      const allUploads = aggregatedKycs.flatMap((kyc) =>
        (kyc.kycUploads || []).map((upload) => ({
          ...upload,
          merchant_id: kyc.merchant_id,
          merchant: kyc.merchant,
          kyc: { id: kyc.id, type: kyc.type }
        }))
      );
      setKycUploads(allUploads);
      setKycMerchantSummaries(mapKycsToMerchantSummaries(aggregatedKycs));
    }
  }, [data]);

  useEffect(() => {
    setKycUploads([]);
    setKycMerchantSummaries([]);
    queryClient.removeQueries('allKycUploads', { exact: true });
    refetch({ refetchPage: (page, index) => index === 0, resetQueries: true });
  }, [searchQuery, status, kycType, dateFilter]);

  const filterByDate = (dateType) => {
    if (dateType) {
      setDateFilter({ from: generatedDates[dateType](), to: generatedDates.now() });
    } else {
      setDateFilter({ from: null, to: null });
    }
  };

  return {
    kycUploads,
    kycMerchantSummaries,
    isFetching,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    searchQuery,
    setSearchQuery,
    isRefetching,
    setStatus,
    status,
    setKycType,
    kycType,
    filterByDate,
    refetch
  };
};

/**
 * @param {string} reference
 * @returns {{ kycUpload: KycUpload, isLoading: boolean, refetch: Function }}
 */
const useKycUpload = (reference) => {
  const { data, isLoading, error, refetch } = useQuery(
    ['kycUpload', reference],
    () => fetchKycUploadByReference(reference),
    {
      enabled: !!reference
    }
  );

  if (error) {
    handleQueryError(error, 'kyc');
  }

  return { kycUpload: data?.data, isLoading, refetch };
};

/**
 * @param {number} merchantId
 * @returns {{ kycUploads: KycUpload[], isLoading: boolean, refetch: Function }}
 */
const useKycUploadsForMerchant = (merchantId) => {
  const { data, isLoading, error, refetch } = useQuery(
    ['kycUploadsForMerchant', merchantId],
    () => fetchKycUploadsForMerchant(merchantId),
    {
      enabled: !!merchantId
    }
  );

  if (error) {
    handleQueryError(error, 'kyc');
  }

  return { kycUploads: data?.data || [], isLoading, refetch };
};

/**
 * @param {number} uploadId
 * @returns {{ comments: Object[], isLoading: boolean }}
 */
const useKycComments = (uploadId) => {
  const { data, isLoading, error } = useQuery(
    ['kycComments', uploadId],
    () => fetchKycComments(uploadId),
    {
      enabled: !!uploadId
    }
  );

  if (error && error.response?.status !== 404) {
    handleQueryError(error, 'kyc');
  }

  return { comments: data?.data || [], isLoading };
};

const useReviewKycUpload = () => {
  const queryClient = useQueryClient();

  return useMutation(reviewKycUpload, {
    onSuccess: () => {
      queryClient.invalidateQueries('allKycUploads');
      queryClient.invalidateQueries('kycUpload');
      queryClient.invalidateQueries('kycUploadsForMerchant');
    }
  });
};

const useAddKycComment = () => {
  const queryClient = useQueryClient();

  return useMutation(addKycComment, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['kycComments', variables.upload_id]);
    }
  });
};

export {
  useAddKycComment,
  useFetchKycDocuments,
  useFetchKycUploads,
  useKycComments,
  useKycInfo,
  useKycUpload,
  useKycUploadsForMerchant,
  useReviewKycUpload
};
